/**
 * Wire types for the VPN debugger API (admin `api/serializers/network_vpn_debug/`).
 *
 * These mirror the server's `diagnosis.build_result()` payload exactly. The step
 * id list is a *contract*, not presentation — the server returns every step in
 * ladder order, and the UI draws them all before any result exists.
 *
 * Keep this union in step with `diagnosis.STEP_ORDER`. A step the server sends
 * and this list omits renders under its raw id with no title; `assertKnownSteps`
 * in vpnDebugConstants turns that silent drift into a console warning.
 */

export type VpnDebugStepId =
  // Added server-side after this UI shipped (actuate_admin #2887, #2899).
  // `tunnel_config` compares admin's stored keys/subnets against the gateway;
  // `tunnel_flow` reports the gateway's per-source packet arrival counters.
  | "tunnel_config"
  | "tunnel_status"
  | "tunnel_flow"
  | "tunnel_reachability"
  | "service_reachability"
  | "authentication"
  | "stream_validation"
  | "media_inspection"
  | "snapshot";

export type VpnDebugStepStatus = "pass" | "fail" | "warn" | "skipped";

/** Statuses the UI adds on top of the wire ones; never sent by the server. */
export type VpnDebugRenderStatus = VpnDebugStepStatus | "running" | "pending";

export interface VpnDebugStep {
  id: VpnDebugStepId;
  order: number;
  title: string;
  status: VpnDebugStepStatus;
  /**
   * Whether this step can move the verdict on its own. The operator payload
   * states it for every step; the customer payload does not carry it at all,
   * which is `null` here - "not stated", never "not decisive".
   */
  decisive: boolean | null;
  reason_code: string | null;
  summary: string;
  diagnosis: string | null;
  next_action: string | null;
  skipped_reason: string | null;
  partial: boolean;
  confidence: string;
  evidence: Record<string, unknown>;
  duration_ms: number | null;
}

export type VpnDebugClassificationCode =
  | "tunnel_down"
  | "routing_firewall"
  | "service_unreachable"
  | "auth_failure"
  | "stream_encoding"
  | "healthy"
  | "inconclusive";

export type VpnDebugFaultOwner = "customer" | "actuate" | "device" | "unknown";

export interface VpnDebugClassification {
  code: VpnDebugClassificationCode;
  label: string;
  owner: VpnDebugFaultOwner;
  confidence: string;
  summary: string;
  evidence_step_ids: string[];
  next_actions: string[];
}

export interface VpnDebugResult {
  schema_version: number;
  tunnel: {
    id: number;
    name: string;
    gateway_name: string;
    gateway_instance_id: string;
  };
  /**
   * Only `host` is common to both planes: the tunnel plane echoes back the
   * address it was given and nothing else, while the device plane adds the ports
   * and the resolved credential source. `username` is null when the camera has
   * no stored secret.
   */
  target: {
    host: string;
    rtsp_port?: number;
    onvif_port?: number;
    camera_id?: number | null;
    credential_source?: string;
    username?: string | null;
  };
  planned_step_ids: VpnDebugStepId[];
  steps: VpnDebugStep[];
  classification: VpnDebugClassification;
  degraded: string[];
  cli_missing: boolean;
}

/** One row of the camera picker. Deliberately carries no password. */
export interface VpnDebugCameraCandidate {
  camera_id: number;
  camera_name: string | null;
  customer_id: number | null;
  customer_name: string | null;
  ip: string;
  rtsp_port: number;
  http_port: number;
  username: string | null;
  has_credentials: boolean;
  codec: string | null;
  stream_type: string | null;
  matched_subnet: string | null;
  in_tunnel_subnet: boolean;
}

export interface VpnDebugTargets {
  tunnel_id: number;
  tunnel_name: string;
  scan_targets: string[];
  /** IPs inside the tunnel's routed ranges. */
  cameras: VpnDebugCameraCandidate[];
  /** Visible to the user but outside those ranges — offered, not hidden. */
  other_cameras: VpnDebugCameraCandidate[];
}

/** 202 body from either plane. */
export interface VpnDebugEnqueueResponse {
  task_id: string;
  status: string;
  tunnel_id: number;
  host: string | null;
  poll_url: string;
  plane?: "tunnel" | "device";
}

/** Poll body. `result` is present only once `status === "success"`. */
export interface VpnDebugPollResponse {
  task_id: string;
  status: "pending" | "success" | "failed";
  result?: VpnDebugResult;
  error?: string;
  detail?: string;
}

export interface VpnDebugCredential {
  username: string;
  password: string;
}

/** Where plane B's credential comes from — same precedence the server applies. */
export type CredentialSource = "camera" | "site" | "none";

export interface FetchedCredential {
  username: string;
  password: string;
  source: CredentialSource;
  /** True when the password came back masked because the user lacks view_passwords. */
  masked: boolean;
}

// ---------------------------------------------------------------------------
// Customer projection
//
// The same endpoints serve two audiences. Actuate staff get the payload above;
// everyone else gets this, which the server builds in
// `network_vpn_debug/customer_projection.py`. It carries no gateway identity,
// no probe evidence, and none of the fleet-wide packet figures the operator
// copy quotes -- the gateways are shared, so those describe other customers.
//
// Identified by `audience`, never by shape or schema_version: both payloads
// carry a version and both move independently.
// ---------------------------------------------------------------------------

export interface VpnDebugCustomerStep {
  id: VpnDebugStepId;
  order: number;
  title: string;
  status: VpnDebugStepStatus;
  reason_code: string | null;
  summary: string;
  detail: string;
  next_action: string | null;
  /** A few curated numbers. Never raw evidence. */
  facts: Record<string, number | string | null>;
}

export interface VpnDebugCustomerVerdict {
  code: string;
  headline: string;
  detail: string;
  /** "who acts next", not "whose fault" -- raw ownership is never sent. */
  owner_label: string;
  next_actions: string[];
}

export interface VpnDebugCustomerResult {
  schema_version: number;
  audience: "customer";
  tunnel: { id: number | null };
  steps: VpnDebugCustomerStep[];
  verdict: VpnDebugCustomerVerdict;
}

/** Either shape, as it comes off the wire. */
export type VpnDebugAnyResult = VpnDebugResult | VpnDebugCustomerResult;

export const isCustomerResult = (
  result: VpnDebugAnyResult | null
): result is VpnDebugCustomerResult =>
  Boolean(result) && (result as VpnDebugCustomerResult).audience === "customer";
